import { gql } from '@apollo/client';
import { captureException } from '@sentry/nextjs';
import { concat, flatten } from 'lodash';
import * as Sentry from '@sentry/react';
import { convertZeroToOneIndexIfNumber, generateUUID } from '../Utils/utils';
import { getCampaignIdTrumpedByUrl } from './campaignId';
import {
  componentContainsCheckboxOptions,
  getValueFromComponent,
} from './components';
import { exposeEventToPartner } from './events';
import { getEncryptedAnswers } from './getEncryptedAnswers';
import { ComponentTypes } from '../Data/Components';
import { isConditionallyActive } from './conditionality';
import {
  getEmailComponent,
  hasPhoneAdmiralId,
} from '../Components/__helpers__/component.helpers';
import Subcategories from '../Data/Subcategories';
import { SELECT_COUNTRY_MAPPED_QUESTION_ID } from '../Data/Country';
import CustomerJourneyType from '../Data/CustomerJourneyType';
import { isPredefinedSupplierBrandAvailable } from '../Components/LegalStatement/legalStatement.helpers';
import { getSupplierMatchComponent } from './supplierMatches';

export const SUBMIT_LEAD_GQL = gql`
  mutation SubmitLead($submission: SubmissionInput!, $visitorId: String!) {
    submitLead(submission: $submission, visitorId: $visitorId) {
      id
      submissionToken
    }
  }
`;

export const SUBMIT_LEAD_WITH_APPOINTMENT_BOOKING_GQL = gql`
  mutation SubmitLead(
    $submission: SubmissionInput!
    $appointmentBooking: AppointmentBookingInput
    $visitorId: String
  ) {
    submitLead(
      submission: $submission
      appointmentBooking: $appointmentBooking
      visitorId: $visitorId
    ) {
      id
      submissionToken
      appointmentId
    }
  }
`;

/**
 *  consentedSuppliersHash and consentedLegalStatementHash are here as props as they do not exist in state and can not be selected from the redux store outside of a hook or react component - and submitLead is a function, which is conditionally called (thus can't be turned into a hook).
 */

export const getUrl = (pageUrl) => {
  if (pageUrl) {
    return pageUrl;
  }

  return window.location !== window.parent.location
    ? document.referrer
    : document.location.href;
};

export const isFormInputComponent = (pageComponent) => {
  const excludedComponents = [
    ComponentTypes.HTML,
    ComponentTypes.HINT,
    ComponentTypes.LOADER,
    ComponentTypes.NEWSLETTER_SIGNUP,
    ComponentTypes.TEXT,
  ];
  const componentType = pageComponent.component.type;
  const componentWasSeen = pageComponent.component?.wasSeen
    ? pageComponent.component?.wasSeen
    : false;

  const componentWasBrowserAutoFilled = pageComponent.component
    ?.browserAutoFilled
    ? pageComponent.component?.browserAutoFilled
    : false;

  const componentWasBrowserAutoFilledAndMounted =
    componentWasSeen && componentWasBrowserAutoFilled;

  return (
    excludedComponents.includes(componentType) === false &&
    (componentWasBrowserAutoFilledAndMounted === true ||
      componentWasBrowserAutoFilled === false)
  );
};

export const exposeFormSubmitEvent = ({
  submissionArgs,
  components,
  eventPublicKeyString,
  submissionResponse,
  iFrameId,
  isDomainInternal,
  submissionPage,
  currentPage,
  appointmentId,
}) => {
  if (eventPublicKeyString && isDomainInternal) {
    /**
     * We want to expose the event to the partner, but we also don't want to expose PII data, i.e. so we should only expose the pii data in the event if the domain is whitelisted (i.e. an internal domain)
     *
     * if eventPublicKeyString is present then the mvfTranslationLayer is ready to receive encryptedAnswers
     */
    getEncryptedAnswers(
      submissionArgs.lead.formComponents,
      components,
      eventPublicKeyString
    )
      .then((encryptedAnswers) => {
        // Add a custom tag to the tracked Chameleon session in Sentry - relevant if a runtime error occurred in this session (to help produce a submission percentage for an issue alert rule via session filtering)
        Sentry.setTag('chameleon_form_submitted', true);
        exposeEventToPartner({
          eventType: 'formSubmit',
          eventObject: {
            submissionId: submissionResponse.id,
            iFrameId,
            encryptedAnswers,
            submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
            fromPage: convertZeroToOneIndexIfNumber(currentPage),
            appointmentId,
          },
        });
      })
      .catch((error) => {
        captureException(error);
      });
  } else {
    /**
     * There are no encryptedAnswers, i.e. no PII so we can send the event
     */
    // Add a custom tag to the tracked Chameleon session in Sentry - relevant if a runtime error occurred in this session (to help produce a submission percentage for an issue alert rule via session filtering)
    Sentry.setTag('chameleon_form_submitted', true);
    exposeEventToPartner({
      eventType: 'formSubmit',
      eventObject: {
        submissionId: submissionResponse.id,
        iFrameId,
        submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
        fromPage: convertZeroToOneIndexIfNumber(currentPage),
        appointmentId,
      },
    });
  }
};

export const exposePartialFormSubmitEvent = ({
  iFrameId,
  partialSubmissionPage,
  submissionId,
  components,
  isDomainInternal,
}) => {
  let email;

  if (isDomainInternal) {
    const emailComponent = getEmailComponent({ components });
    email = emailComponent?.input?.value;
  } else {
    email = '********';
  }

  exposeEventToPartner({
    eventType: 'partialFormSubmit',
    eventObject: {
      iFrameId,
      partialSubmissionPage: convertZeroToOneIndexIfNumber(
        partialSubmissionPage
      ),
      submissionId,
      email,
    },
  });
};

const isConditionallyHiddenSupplierMatch = (pageComponent, pageComponents) => {
  return (
    pageComponent.component.type === 'supplier-match' &&
    !isConditionallyActive(pageComponents, pageComponent)
  );
};

export const convertComponentToSubmissionObject = (
  pageComponent,
  pageComponents
) => {
  const submission = {
    id: pageComponent.component.id.toString(),
  };

  const value = getValueFromComponent(pageComponent);

  if (componentContainsCheckboxOptions(pageComponent)) {
    // Currently we do not filter out conditionally hidden components but consider supplier match components as unselected if they are conditionally hidden. We may look to extending this to all conditionally hidden components in the future.
    return {
      ...submission,
      value:
        value &&
        value.length > 0 &&
        !isConditionallyHiddenSupplierMatch(pageComponent, pageComponents)
          ? value
          : [''],
      type: 'multiple',
    };
  }

  return {
    ...submission,
    value: [value || ''],
    type: 'single',
  };
};

const getUrlForSubmission = (url, useCidFromURL = true) => {
  if (useCidFromURL === false) {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('cid');
    return urlObj.toString();
  }

  return url;
};

const convertMatchesIntoBrands = ({ brandsOptionIds, matches }) => {
  const selectedBrands = brandsOptionIds.flatMap((optionId) => {
    return matches.find((potentialMatch) => {
      return potentialMatch.brandId === optionId;
    });
  });

  return selectedBrands.map((brandMatch) => {
    return {
      brandId: brandMatch.brandId,
      brandRevisionId: brandMatch.brandRevisionId,
    };
  });
};

const getBrandsShownToCustomer = (
  formInputComponents,
  customerBrandMatches,
  subcategoryBrandMatches,
  supplierMatchSelectionEnabled,
  isPredefinedSupplierWithBrandAvailable
) => {
  if (isPredefinedSupplierWithBrandAvailable) {
    const brandMatch = customerBrandMatches[0];
    return [
      {
        brandId: brandMatch.brandId,
        brandRevisionId: brandMatch.brandRevisionId,
      },
    ];
  }
  const supplierMatchComponents = formInputComponents.filter(
    (component) =>
      component.component.type === 'supplier-match' &&
      !isConditionallyHiddenSupplierMatch(component, formInputComponents)
  );

  if (!supplierMatchSelectionEnabled || supplierMatchComponents.length === 0) {
    return undefined;
  }

  const shownBrandsOptionIds = flatten(
    supplierMatchComponents.map((component) => {
      return component.input.options.map((option) => {
        return option.id;
      });
    })
  );

  if (customerBrandMatches.some((match) => match.brandId)) {
    // We have customer matches with brand IDs, so send the new fields in the submission
    return convertMatchesIntoBrands({
      brandsOptionIds: shownBrandsOptionIds,
      matches: customerBrandMatches,
    });
  }

  if (subcategoryBrandMatches.some((match) => match.brandId)) {
    // We have failed to obtain customer matches but have access to the highest revenue fallback brands from capture so can use these.
    return convertMatchesIntoBrands({
      brandsOptionIds: shownBrandsOptionIds,
      matches: subcategoryBrandMatches,
    });
  }

  // Temporary workaround: If we do not have brand matches in the form of customer matches or the highest revenue fallback then we will need to utilise the admiral questions. This could be possible when opening up a new subcategory with no live suppliers. In the future we will want to handle this situation in another way such as simply failing submission and displaying some helpful message to the customer.

  const brandMatches = [];

  const getMatchingBrand = (matches) => (optionId) => {
    return matches.find((potentialMatch) => {
      return potentialMatch.optionId === optionId;
    });
  };

  // Check customer matches first to see if the user has selected any brands from there
  if (customerBrandMatches.length > 0) {
    brandMatches.push(
      ...shownBrandsOptionIds.flatMap(getMatchingBrand(customerBrandMatches))
    );
  }

  // If there are no customer matches, check the subcategory fallback matches
  if (customerBrandMatches.length === 0 && subcategoryBrandMatches.length > 0) {
    brandMatches.push(
      ...shownBrandsOptionIds.flatMap(getMatchingBrand(subcategoryBrandMatches))
    );
  }

  return brandMatches.map((brandMatch) => {
    return {
      brandFilterValue: brandMatch.brandFilterValue,
    };
  });
};

export const getBrandsConsentedByCustomer = (
  components,
  customerBrandMatches,
  subcategoryBrandMatches,
  supplierMatchSelectionEnabled,
  isPredefinedSupplierWithBrandAvailable
) => {
  if (isPredefinedSupplierWithBrandAvailable) {
    const brandMatch = customerBrandMatches[0];
    return [
      {
        brandId: brandMatch.brandId,
        brandRevisionId: brandMatch.brandRevisionId,
      },
    ];
  }

  const supplierMatchComponents = components.filter(
    (component) =>
      component.component.type === 'supplier-match' &&
      !isConditionallyHiddenSupplierMatch(component, components)
  );

  if (!supplierMatchSelectionEnabled || supplierMatchComponents.length === 0) {
    return undefined;
  }

  const selectedBrandsOptionIds = flatten(
    supplierMatchComponents.map((component) => {
      return component.input.selectedOptions;
    })
  );

  if (customerBrandMatches.some((match) => match.brandId)) {
    // We have customer matches with brand IDs, so send the new fields in the submission
    return convertMatchesIntoBrands({
      brandsOptionIds: selectedBrandsOptionIds,
      matches: customerBrandMatches,
    });
  }

  if (subcategoryBrandMatches.some((match) => match.brandId)) {
    // We have failed to obtain customer matches but have access to the highest revenue fallback brands from capture so can use these.
    return convertMatchesIntoBrands({
      brandsOptionIds: selectedBrandsOptionIds,
      matches: subcategoryBrandMatches,
    });
  }

  // Temporary workaround: If we do not have brand matches in the form of customer matches or the highest revenue fallback then we will need to utilise the admiral questions. This could be possible when opening up a new subcategory with no live suppliers. In the future we will want to handle this situation in another way such as simply failing submission and displaying some helpful message to the customer.

  const brandMatches = [];

  const getMatchingBrand = (matches) => (optionId) => {
    return matches.find((potentialMatch) => {
      return potentialMatch.optionId === optionId;
    });
  };

  // Check customer matches first to see if the user has selected any brands from there
  if (customerBrandMatches.length > 0) {
    brandMatches.push(
      ...selectedBrandsOptionIds.flatMap(getMatchingBrand(customerBrandMatches))
    );
  }

  // If there are no customer matches, check the subcategory fallback matches
  if (customerBrandMatches.length === 0 && subcategoryBrandMatches.length > 0) {
    brandMatches.push(
      ...selectedBrandsOptionIds.flatMap(
        getMatchingBrand(subcategoryBrandMatches)
      )
    );
  }

  return brandMatches.map((brandMatch) => {
    return {
      brandFilterValue: brandMatch.brandFilterValue,
    };
  });
};

const getDeselectedBrands = (
  selectedBrands,
  brandsShown,
  supplierMatchSelectionEnabled,
  selectAndConnectBrand,
  predefinedSupplierId,
  isPredefinedSupplierWithBrandAvailable
) => {
  if (isPredefinedSupplierWithBrandAvailable && !selectAndConnectBrand) {
    return [];
  }
  if (
    !supplierMatchSelectionEnabled ||
    typeof selectedBrands !== 'object' ||
    typeof brandsShown !== 'object' ||
    selectAndConnectBrand ||
    predefinedSupplierId
  ) {
    return undefined;
  }

  const selectedBrandIds = selectedBrands.map((brand) => brand.brandId);

  return brandsShown.filter(
    (brand) => !selectedBrandIds.includes(brand.brandId)
  );
};

const getShownLegalStatementHash = ({
  multipleSupplierConsentedLegalStatementHash,
  singleSupplierConsentedLegalStatementHash,
  consentedLegalStatementHash,
  selectAndConnectBrand,
  customerBrandMatches,
  supplierId,
  isPartial,
}) => {
  if (isPartial) return consentedLegalStatementHash;

  if (selectAndConnectBrand) {
    // Select and connect components always show a multi supplier statement
    return multipleSupplierConsentedLegalStatementHash;
  }

  if (
    isPredefinedSupplierBrandAvailable({
      supplierId,
      customerMatches: customerBrandMatches,
    })
  ) {
    return singleSupplierConsentedLegalStatementHash;
  }

  return consentedLegalStatementHash;
};

const getCustomerJourneyType = (
  selectAndConnectBrand,
  supplierMatchJourney,
  predefinedSupplierId
) => {
  if (selectAndConnectBrand) {
    return CustomerJourneyType.SELECT_AND_CONNECT;
  }
  if (predefinedSupplierId) {
    return CustomerJourneyType.PREDEFINED_SUPPLIER;
  }
  if (supplierMatchJourney) {
    return CustomerJourneyType.BUNDLED_CONSENT_WITH_DESELECTIONS;
  }
  return CustomerJourneyType.BUNDLED_CONSENT_NO_MATCHING;
};

/**
 * This concerns whether customers only had the option to be matched to a single supplier during their journey either through tcpa supplier matching or by having their supplier id predefined for the form.
 */
const isSingleSupplierPresented = (
  shouldIncludeSupplierMatch,
  predefinedSupplierId,
  brandsShownToCustomer
) => {
  if (!shouldIncludeSupplierMatch) return false;

  if (predefinedSupplierId) return true;

  return Boolean(
    typeof brandsShownToCustomer === 'object' &&
      brandsShownToCustomer.length === 1
  );
};

const areSupplierMatchesConditionallyActive = (
  components,
  shouldIncludeSupplierMatch
) => {
  if (!shouldIncludeSupplierMatch) return false;
  const firstSupplierMatchComponent = getSupplierMatchComponent({ components });
  return Boolean(
    firstSupplierMatchComponent &&
      isConditionallyActive(components, firstSupplierMatchComponent)
  );
};

const isSupplierMatchJourney = (
  components,
  shouldIncludeSupplierMatch,
  predefinedSupplierId,
  customerBrandMatches
) =>
  Boolean(
    areSupplierMatchesConditionallyActive(
      components,
      shouldIncludeSupplierMatch
    ) ||
      isPredefinedSupplierBrandAvailable({
        supplierId: predefinedSupplierId,
        customerMatches: customerBrandMatches,
      })
  );

export const getSubmissionArgs = ({
  ancestorSid,
  campaignId,
  countryCode,
  components,
  consentedSuppliersHash,
  consentedLegalStatementHash,
  formId,
  pageUrl,
  referrerUrl,
  phoneNumberCountryCode,
  sourceSite,
  testMode,
  widgetLabel,
  useCidFromURL,
  parentPageCookies,
  oneTrustLocale,
  customerBrandMatches,
  subcategoryBrandMatches,
  multipleSupplierConsentedLegalStatementHash,
  singleSupplierConsentedLegalStatementHash,
  consentCertification,
  supplierId,
  consentStatement,
  privacyPolicyUrl,
  subcategoryName,
  subcategoryId,
  matchLevel,
  selectAndConnectBrand,
  isPartial,
  supplierMatchSelectionEnabled,
  themeName,
  featureFlags,
}) => {
  const componentsUsedInSubmission = Object.values(components).filter(
    (component) => {
      if (isPartial && hasPhoneAdmiralId(component)) {
        // We do not include phone number components in partial submissions since we do not have legal consent to collect them.
        return false;
      }

      return isFormInputComponent(component);
    }
  );

  const submissions = componentsUsedInSubmission.map((component) =>
    convertComponentToSubmissionObject(component, components)
  );

  const url = getUrl(pageUrl);

  const shownLegalStatementHash = getShownLegalStatementHash({
    multipleSupplierConsentedLegalStatementHash,
    singleSupplierConsentedLegalStatementHash,
    consentedLegalStatementHash,
    selectAndConnectBrand,
    customerBrandMatches,
    supplierId,
    isPartial,
  });

  const emailComponent = getEmailComponent({ components });
  const email = emailComponent?.input?.value;

  const selectedCountryIsUSA = Object.values(components).some(
    ({ component, input }) =>
      component.mappedQuestionId === SELECT_COUNTRY_MAPPED_QUESTION_ID &&
      input?.value?.trim() === 'USA'
  );

  const supplierMatchJourneyBlockedByNonUSARemovalsAutoqualifiedJourney =
    subcategoryId === Subcategories.REMOVALS_AUTOQUALIFIED.id &&
    phoneNumberCountryCode !== 'US' &&
    !selectedCountryIsUSA;

  const isPredefinedSupplierWithBrandAvailable =
    isPredefinedSupplierBrandAvailable({
      supplierId,
      customerMatches: customerBrandMatches,
    });

  const shouldIncludeSupplierMatch =
    (supplierMatchSelectionEnabled ||
      isPredefinedSupplierWithBrandAvailable ||
      selectAndConnectBrand) &&
    !supplierMatchJourneyBlockedByNonUSARemovalsAutoqualifiedJourney;

  let brandsConsentedByCustomer = getBrandsConsentedByCustomer(
    componentsUsedInSubmission,
    customerBrandMatches,
    subcategoryBrandMatches,
    supplierMatchSelectionEnabled,
    isPredefinedSupplierWithBrandAvailable
  );

  const brandsShownToCustomer = getBrandsShownToCustomer(
    componentsUsedInSubmission,
    customerBrandMatches,
    subcategoryBrandMatches,
    supplierMatchSelectionEnabled,
    isPredefinedSupplierWithBrandAvailable
  );

  const deselectedBrands = getDeselectedBrands(
    brandsConsentedByCustomer,
    brandsShownToCustomer,
    supplierMatchSelectionEnabled,
    selectAndConnectBrand,
    supplierId,
    isPredefinedSupplierWithBrandAvailable
  );

  if (selectAndConnectBrand) {
    // Select and connect brands override the customer brands
    brandsConsentedByCustomer = [selectAndConnectBrand];
  }

  let shownToCustomer;

  if (
    Array.isArray(brandsShownToCustomer) &&
    brandsShownToCustomer.length > 0 &&
    !selectAndConnectBrand
  ) {
    shownToCustomer = {
      matchLevel:
        supplierId && !selectAndConnectBrand ? 'PREDEFINED' : matchLevel,
      matches: brandsShownToCustomer,
    };
  }

  const supplierMatchJourney = shouldIncludeSupplierMatch
    ? isSupplierMatchJourney(
        components,
        shouldIncludeSupplierMatch,
        supplierId,
        customerBrandMatches
      )
    : false;

  return {
    lead: {
      countryCode,
      cookies: parentPageCookies,
      campaignId:
        getCampaignIdTrumpedByUrl(campaignId, url, useCidFromURL) ?? undefined,
      consentedSuppliersHash:
        selectAndConnectBrand || isPredefinedSupplierWithBrandAvailable
          ? undefined
          : consentedSuppliersHash,
      consentedLegalStatementHash: shownLegalStatementHash,
      formId: String(formId),
      formComponents: concat(submissions, {
        id: String(sourceSite.name).match(/[0-9]+/)[0],
        type: 'single',
        value: [sourceSite.value],
      }),
      supplierMatchJourney,
      singleSupplierPresented: isSingleSupplierPresented(
        shouldIncludeSupplierMatch,
        supplierId,
        brandsShownToCustomer
      ),
      originId: process.env.NEXT_PUBLIC_SUBMISSION_ORIGIN_ID,
      phoneNumberCountryCode: phoneNumberCountryCode ?? '',
      rsid: ancestorSid !== null ? ancestorSid : undefined, // we track a resubmissions original sid on the rsid value of a lead - can be undefined
      testMode: testMode ?? false,
      url: getUrlForSubmission(url, useCidFromURL),
      referrerUrl,
      uuid: generateUUID(),
      widgetLabel, // default of empty string
      oneTrustLocale,
      brandsConsentedByCustomer: shouldIncludeSupplierMatch
        ? brandsConsentedByCustomer
        : undefined,
      brandsDeselectedByCustomer: shouldIncludeSupplierMatch
        ? deselectedBrands
        : undefined,
      customerJourneyType: getCustomerJourneyType(
        selectAndConnectBrand,
        supplierMatchJourney,
        supplierId
      ),
      shownToCustomer: shouldIncludeSupplierMatch ? shownToCustomer : undefined,
      consentCertification,
      consentStatement,
      privacyPolicyUrl,
      email,
      subcategoryName,
      isPartial,
      theme: themeName,
      featureFlags,
    },
  };
};

export const getPartialSubmission = (components) => {
  const formInputComponents =
    Object.values(components).filter(isFormInputComponent);
  return formInputComponents.map((component) => {
    return {
      mappedQuestionId: component.component?.mappedQuestionId,
      value: getValueFromComponent(component),
    };
  });
};

export const shouldShowSubmissionBlockPage = (
  currentPage,
  submissionBlocks
) => {
  return submissionBlocks.some((block) => block.page < currentPage);
};
